/**
 * TemplatePickerModal — P0-5 · Mono-destinatario Template Picker.
 *
 * Aperto dal thread quando la Customer Service Window è chiusa. Consente allo
 * staff con permesso `staff.messaging.templates.send` di ricontattare un
 * singolo cliente inviando un template WhatsApp approvato senza passare dal
 * flow batch admin.
 *
 * Contratto UX:
 *   - Carica templates approved da GET /api/admin/messaging/templates
 *   - Utente sceglie template + language + (opzionale) first_name
 *   - Invia POST /api/staff/messaging/whatsapp/conversations/{id}/send-template
 *   - Al successo chiude la modale e triggera il refresh del thread
 *
 * NOTA: NON è un secondo template system parallelo — usa gli stessi endpoint
 * `templates.list` (admin) + un nuovo mono-endpoint dedicato al thread.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { X, Send, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

export function TemplatePickerModal({
  conversationId,
  defaultFirstName = "",
  onClose,
  onSent,
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null);   // {name, language, ...}
  const [firstName, setFirstName] = useState(defaultFirstName || "");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    (async () => {
      try {
        const { data } = await api.get("/admin/messaging/templates");
        if (!mounted.current) return;
        const items = (data.templates || []).filter(
          (t) => String(t.status || "").toUpperCase() === "APPROVED"
        );
        setTemplates(items);
        if (items.length > 0) setSelected(items[0]);
      } catch (e) {
        if (mounted.current) setError(formatApiError(e));
      } finally {
        if (mounted.current) setLoading(false);
      }
    })();
    return () => { mounted.current = false; };
  }, []);

  const submit = useCallback(async () => {
    if (!selected || sending) return;
    setSending(true);
    setSendError(null);
    try {
      const { data } = await api.post(
        `/staff/messaging/whatsapp/conversations/${conversationId}/send-template`,
        {
          template_name: selected.name,
          template_language: selected.language,
          first_name: (firstName || "").trim() || null,
        }
      );
      onSent?.(data);
      onClose?.();
    } catch (e) {
      if (mounted.current) setSendError(formatApiError(e));
    } finally {
      if (mounted.current) setSending(false);
    }
  }, [conversationId, selected, firstName, sending, onSent, onClose]);

  const languageBadge = useMemo(() => {
    if (!selected) return null;
    return (
      <span className="ml-2 rounded-full bg-sand-200 px-2 py-0.5 text-xs text-ink-700">
        {selected.language}
      </span>
    );
  }, [selected]);

  return (
    <div
      data-testid="template-picker-backdrop"
      onClick={onClose}
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
    >
      <div
        data-testid="template-picker-modal"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg rounded-3xl bg-white shadow-xl border-2 border-sand-200 max-h-[85vh] flex flex-col"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-sand-200">
          <div>
            <div className="text-lg font-semibold text-ink-900">
              Invia template WhatsApp
            </div>
            <div className="text-xs text-ink-500">
              La finestra di 24h è scaduta · usa un template approvato per ricontattare il cliente
            </div>
          </div>
          <button
            data-testid="template-picker-close"
            onClick={onClose}
            className="p-2 rounded-full hover:bg-sand-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {loading && (
            <div data-testid="template-picker-loading"
                 className="text-sm text-ink-500 flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Caricamento template…
            </div>
          )}
          {error && (
            <div data-testid="template-picker-error"
                 className="rounded-2xl border-2 border-red-200 bg-red-50 p-3 text-sm text-red-800">
              {error}
            </div>
          )}
          {!loading && !error && templates.length === 0 && (
            <div data-testid="template-picker-empty"
                 className="rounded-2xl border-2 border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              Nessun template approvato disponibile. Chiedi a un admin di sincronizzare i template.
            </div>
          )}
          {!loading && templates.length > 0 && (
            <>
              <div>
                <label className="block text-xs font-semibold text-ink-600 mb-1">
                  Template
                </label>
                <select
                  data-testid="template-picker-select"
                  value={selected ? `${selected.name}|${selected.language}` : ""}
                  onChange={(e) => {
                    const [n, l] = e.target.value.split("|");
                    const t = templates.find(
                      (x) => x.name === n && x.language === l
                    );
                    setSelected(t || null);
                  }}
                  className="w-full rounded-2xl border-2 border-sand-300 bg-white px-3 py-2 text-sm"
                >
                  {templates.map((t) => (
                    <option
                      key={`${t.name}|${t.language}`}
                      value={`${t.name}|${t.language}`}
                    >
                      {t.name} ({t.language})
                    </option>
                  ))}
                </select>
                {selected && languageBadge && (
                  <div className="mt-1 text-xs text-ink-500">
                    Lingua: {languageBadge}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold text-ink-600 mb-1">
                  Nome del cliente (opzionale)
                </label>
                <input
                  data-testid="template-picker-firstname"
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Es. Mario"
                  className="w-full rounded-2xl border-2 border-sand-300 bg-white px-3 py-2 text-sm"
                />
                <div className="mt-1 text-xs text-ink-500">
                  Se il template usa {"{{1}}"} verrà sostituito con questo nome.
                </div>
              </div>

              {selected?.body_text && (
                <div>
                  <label className="block text-xs font-semibold text-ink-600 mb-1">
                    Anteprima
                  </label>
                  <div data-testid="template-picker-preview"
                       className="rounded-2xl border-2 border-sand-200 bg-sand-50 p-3 text-sm text-ink-800 whitespace-pre-wrap">
                    {selected.body_text.replace(/{{\s*1\s*}}/g, firstName || "…")}
                  </div>
                </div>
              )}

              {sendError && (
                <div data-testid="template-picker-send-error"
                     className="rounded-2xl border-2 border-red-200 bg-red-50 p-3 text-sm text-red-800">
                  {sendError}
                </div>
              )}
            </>
          )}
        </div>

        <div className="px-5 py-4 border-t border-sand-200 flex items-center justify-end gap-2">
          <button
            data-testid="template-picker-cancel"
            onClick={onClose}
            disabled={sending}
            className="px-4 py-2 rounded-full border-2 border-sand-300 text-ink-700 text-sm font-semibold hover:bg-sand-50 disabled:opacity-50"
          >
            Annulla
          </button>
          <button
            data-testid="template-picker-send"
            onClick={submit}
            disabled={!selected || sending || loading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-bronze-600 text-white text-sm font-semibold hover:bg-bronze-700 disabled:bg-sand-300 disabled:cursor-not-allowed"
          >
            {sending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Invio…
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                Invia template
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default TemplatePickerModal;
